// LICENSE_CODE ZON
/*jslint ts:true, react: true*/
import React, {
    useRef, useEffect, MutableRefObject,
    ChangeEvent, CSSProperties} from 'react';
import cn from 'classnames';

import styles from './range_input.module.less';
import {use_theme} from '../settings_ctx';

type i_range_input_props = {
    disabled?: boolean;
    value: number;
    max: number;
    min: number;
    step: number;
    on_change: (num:number)=>void;
    tracker_with_ref?:
        (ref: MutableRefObject<HTMLDivElement|null>, value: number)
        =>JSX.Element;
    container_cls?: string;
    thumb_size?: number;
}

export const Range_input = ({value, max, min, step, on_change, disabled=false,
    tracker_with_ref, container_cls, thumb_size = 20}:i_range_input_props)=>{
    const tracker_ref = useRef<HTMLDivElement>(null);
    const input_ref = useRef<HTMLInputElement>(null);
    const {theme_name} = use_theme();

    const sync_tracker = (rel:number)=>{
        if (tracker_ref.current && input_ref.current)
        {
            const tracker_width = tracker_ref.current
                .getBoundingClientRect().width;
            const input_width = input_ref.current
                .getBoundingClientRect().width;
            const left = 100*(rel -
                0.5*(tracker_width -1 + thumb_size*(2*rel-1)) / input_width);
            tracker_ref.current.style.left = left + '%';
        }
    };

    const sync_progress = (rel:number)=>{
        if (input_ref.current)
        {
            const off = Math.round(rel*100);
            input_ref.current.style.background =
                `linear-gradient(to right, var(--gradient-slider-start) ${
                    off}%, var(--gradient-slider-end) ${off}%)`;
        }
    };

    const on_input_change = (ev: ChangeEvent<HTMLInputElement>)=>{
        const val = +ev.target.value;
        if (on_change)
            on_change(val);
    };

    useEffect(()=>{
        const rel = value/(max-min+1);
        if (tracker_with_ref)
            sync_tracker(disabled ? 0.5 : rel);
        sync_progress(disabled ? 0 : rel);
    }, [value, disabled]);

    useEffect(()=>{
        if (tracker_ref.current)
        {
            tracker_ref.current.onmousedown = disabled ? null
            : (event: MouseEvent)=>{
                event.preventDefault();
                if (!tracker_ref.current || !input_ref.current)
                    return;
                const tracker_width = tracker_ref.current
                    .getBoundingClientRect().width;
                const input_width = input_ref.current
                    .getBoundingClientRect().width;
                const margin = (tracker_width - thumb_size)/2;
                let shift_x = event.clientX
                         - tracker_ref.current.getBoundingClientRect().left;

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);

                function onMouseMove(evt: MouseEvent){
                    if (!tracker_ref.current || !input_ref.current)
                        return;
                    let left_px = evt.clientX - shift_x
                            - input_ref.current.getBoundingClientRect().left;
                    if (left_px < - margin)
                        left_px = - margin;
                    const right_edge = input_ref.current.offsetWidth
                                    - tracker_ref.current.offsetWidth
                                    + margin;
                    if (left_px > right_edge)
                        left_px = right_edge;
                    const left = left_px * 100 / input_width;
                    tracker_ref.current.style.left = left + '%';
                    let rel = (left_px + margin)/ (right_edge + margin);

                    const val = Math.round((max-min) * rel) + min;
                    on_change(val);
                }

                function onMouseUp(){
                    document.removeEventListener('mouseup', onMouseUp);
                    document.removeEventListener('mousemove', onMouseMove);
                }
            };

            tracker_ref.current.ondragstart = ()=>false;
        }
    }, [disabled]);
    const style = {'--thumb_size': `${thumb_size}px`} as CSSProperties;
    return <div style={style}
      className={cn(styles.range_input_container,
      container_cls, disabled && styles.disabled,
      theme_name=='toolip' && styles.toolip
      )}>
        {tracker_with_ref && tracker_with_ref(tracker_ref, value)}
        <input type='range' min={min} max={max} step={step}
          value={disabled ? 0.5*(max-min) : value}
          className={styles.range_input} disabled={disabled}
          onChange={on_input_change} ref={input_ref}/>
    </div>;
};
